import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import AppRoutes from '../../components/app-routes/AppRoutes';
import HeaderNavbar from '../header/HeaderNavbar';
import AccountMenuPopup from '../account-menu-popup/AccountMenuPopup';
import UserFlowTimeline from '../timeline/UserFlowTimeline';
import SideNav from '../../screens/dashboard-revamp/components/side-nav/SideNav';
import { InstaremAvatar } from '../instarem-avatar/InstaremAvatar';
import { ToggleFeatureConsumer, useToggleFeat } from '../../toggleFeature';
import { toggleSidebar } from '../../redux/actions/ui-trigger/ui-trigger-action';
import useShowTimeline from '../../hooks/useShowTimeline/useShowTimeline';
import { selectUiTriggerStateReducer, selectUserSessionReducer } from '@Redux/selectors';
import { CHEVRON_RIGHT_ICON, NEW_INSTAREM_LOGO_ICON, PROFILE_ICON_NEW } from '@Images';
import {
  CORPORATE,
  DEVICE_MOBILE,
  GOOGLE_RECAPTCHA_PUBLIC_URL,
  DASHBOARD_URL,
  CONSENT_PERMISSION_URL,
  HEADER_FOOTER_IGNORED_URLS,
  ADD_PAYEE_URL,
  TRANSFER_MONEY_URL,
  REGISTERED,
  INITIATED
} from '../../utils/constants';
import {
  AppBodyContainer,
  AppContentContainer,
  AppRoutesContainer,
  ProfileContainer,
  TimelineContainer,
  TopBarContainer,
  TopBarLogoContainer
} from './app.styles';
import type { AppDispatch } from '../../store.type';
import { AppContentProps } from './AppContent.types';

const AppContent = ({
  user_session,
  currentScreen,
  screens,
  timeline,
  referred_by_referral_code,
  account_type,
  additional_info,
  headerRef,
  toggleMenuRef,
  userStatusReducer,
  useOldNavBar
}: AppContentProps) => {
  const featureState = useToggleFeat();
  const shouldRenderTimeline = useShowTimeline();
  const { pathname } = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { device } = useSelector(selectUiTriggerStateReducer);
  const { is_user_authenticated: isUserAuthenticated } = useSelector(selectUserSessionReducer);
  const {
    remitter_individual_last_name: userLastName,
    remitter_individual_first_name: userFirstName,
    status: userStatus
  } = userStatusReducer;

  const hard_sme_migrated = account_type === CORPORATE && additional_info?.hard_sme_migrated;
  const shouldShowSideNav =
    pathname !== GOOGLE_RECAPTCHA_PUBLIC_URL &&
    pathname !== ADD_PAYEE_URL &&
    pathname !== TRANSFER_MONEY_URL &&
    device !== DEVICE_MOBILE &&
    (userFirstName || [REGISTERED, INITIATED].includes(userStatus));

  if (useOldNavBar) {
    return (
      <ToggleFeatureConsumer>
        {pathname !== GOOGLE_RECAPTCHA_PUBLIC_URL ? (
          <HeaderNavbar
            provideToggleMenuParams={(navbar: any) =>
              (toggleMenuRef.current = () => {
                let {
                  state: { openMenu, notification },
                  toggleMenu,
                  closeNotification
                } = navbar;
                openMenu && toggleMenu();
                notification && closeNotification && closeNotification();
              })
            }
            hard_sme_migrated={hard_sme_migrated}
            headerRef={headerRef}
            device={device}
          />
        ) : (
          <></>
        )}
        <AppRoutes
          countryCode={user_session.country_code}
          hard_sme_migrated={hard_sme_migrated}
          headerRef={headerRef}
        />
      </ToggleFeatureConsumer>
    );
  }

  const isTopBarHidden =
    pathname === GOOGLE_RECAPTCHA_PUBLIC_URL ||
    window.location.href.includes(CONSENT_PERMISSION_URL) ||
    (!HEADER_FOOTER_IGNORED_URLS.includes(pathname) &&
      (/signin|signup|invite\/|password|logout|feedback\//gi.test(pathname) || pathname === '/'));

  const isInNativeWebView = !!((window as any).ReactNativeWebView || (window as any).WebViewBridge);

  return (
    <AppContentContainer>
      {!isTopBarHidden && (
        <TopBarContainer>
          <TopBarLogoContainer>
            <Link to={DASHBOARD_URL}>
              <img src={NEW_INSTAREM_LOGO_ICON} alt="instarem" />
            </Link>
          </TopBarLogoContainer>
          {shouldRenderTimeline && (
            <TimelineContainer>
              <UserFlowTimeline
                user_session={user_session}
                currentScreen={currentScreen}
                screens={screens}
                timeline={timeline}
                featureState={featureState}
                referred_by_referral_code={referred_by_referral_code}
              />
            </TimelineContainer>
          )}
          {!isInNativeWebView &&
            (userFirstName ? (
              device === DEVICE_MOBILE ? (
                <ProfileContainer onClick={() => dispatch(toggleSidebar())}>
                  <InstaremAvatar
                    height={48}
                    width={48}
                    name={`${userFirstName} ${userLastName}`}
                  />
                  <span className="profile-container__user-name">{`${userFirstName} ${userLastName}`}</span>
                  <img
                    className="profile-container__right-icon"
                    alt="right-icon"
                    src={CHEVRON_RIGHT_ICON}
                  />
                </ProfileContainer>
              ) : (
                <AccountMenuPopup>
                  <ProfileContainer>
                    <InstaremAvatar
                      height={48}
                      width={48}
                      name={`${userFirstName} ${userLastName}`}
                    />
                    <span className="profile-container__user-name">{`${userFirstName} ${userLastName}`}</span>
                    <img
                      className="profile-container__right-icon"
                      alt="right-icon"
                      src={CHEVRON_RIGHT_ICON}
                    />
                  </ProfileContainer>
                </AccountMenuPopup>
              )
            ) : device === DEVICE_MOBILE ? (
              <ProfileContainer onClick={() => dispatch(toggleSidebar())}>
                <img
                  className="profile-container__profile-icon"
                  src={PROFILE_ICON_NEW}
                  alt="profile"
                />
              </ProfileContainer>
            ) : isUserAuthenticated ? (
              <AccountMenuPopup>
                <ProfileContainer>
                  <img
                    className="profile-container__profile-icon"
                    src={PROFILE_ICON_NEW}
                    alt="profile"
                  />
                </ProfileContainer>
              </AccountMenuPopup>
            ) : (
              <ProfileContainer>
                <img
                  className="profile-container__profile-icon"
                  src={PROFILE_ICON_NEW}
                  alt="profile"
                />
              </ProfileContainer>
            ))}
        </TopBarContainer>
      )}
      <AppBodyContainer>
        {shouldShowSideNav && <SideNav />}
        <AppRoutesContainer>
          <AppRoutes
            featureState={featureState}
            countryCode={user_session.country_code}
            hard_sme_migrated={hard_sme_migrated}
            headerRef={headerRef}
          />
        </AppRoutesContainer>
      </AppBodyContainer>
    </AppContentContainer>
  );
};

export default AppContent;
